import { ContactSelect } from '@components'

import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { BillState } from '@features/bills/enums/billState'
import { Contact } from '@features/contacts/types/contact'

import { BillFormFieldsChangeSource } from '../../../../../../enums/billFormFieldsChangeSource'
import { useFormContext, useWatch } from '../../../../../../hooks'
import { useBillForm } from '../../contexts/billFormContext'
import { useVendorAutoComplete } from '../../hooks/useVendorAutoComplete'
import { BillFormSchema } from '../../utils/formData'

interface BillVendorProps {
  onCreate?: () => void
  withReferenceNumberField?: boolean
}

export const BillVendor = ({ onCreate, withReferenceNumberField }: BillVendorProps): ReactElement => {
  const { t } = useTranslation()
  const { control, FormItem, setValue } = useFormContext<BillFormSchema>()
  const { isCreditNoteFromBill } = useBillForm()
  const billState = useWatch({ control, name: 'state' })
  const creditedBillId = useWatch({ control, name: 'creditedBillId' })
  const vendor = useWatch({ control, name: 'vendor' })
  const vendorSuggestions = useWatch({ control, name: 'vendorSuggestions' })
  const isNewVendor = !!vendor && !vendor.id
  const { autoCompleteWithVendorData } = useVendorAutoComplete()

  const handleContactChange = useCallback(
    // both adding and removing vendor cause the form to change autocompleted values, to remove or change it accordingly
    (contact: Partial<Contact> | undefined) => {
      autoCompleteWithVendorData(contact, BillFormFieldsChangeSource.Vendor)
    },
    [autoCompleteWithVendorData],
  )

  return (
    <FormItem
      name="vendor"
      label={t('bill.edit.form.vendor')}
      extra={isNewVendor ? t('bill.edit.form.vendor.new_supplier_caption') : undefined}
      itemColumnStart={withReferenceNumberField ? 1 : undefined}
      itemColumnEnd={withReferenceNumberField ? 4 : undefined}
      render={({ field: { value, onChange, ...props } }) => (
        <ContactSelect
          {...props}
          allowClear
          allowCreateNew
          isSupplier
          newSupplierSuggestions={vendorSuggestions}
          onCreateNew={onCreate}
          onSelect={(id?: string, value?: Partial<Contact>) => {
            onChange(value)
            setValue('vendorSuggestions', undefined)
            handleContactChange(value)
          }}
          selectedId={value?.id}
          selectedValue={value}
          disabled={billState === BillState.Approved || isCreditNoteFromBill || !!creditedBillId}
          withSearch
        />
      )}
    />
  )
}
